import DOMPurify from "dompurify";

const emoticonMap = {
  thinking: { srcset: 1 },
  woodenReno: { srcset: 1 },
  "jaraxxus-thinking": { srcset: 1 },
  jaraxxus: { srcset: 1 },
  razorpetal: { srcset: 1 },
  bananas: {},
  "annoy-o-tron": { srcset: 1 },
  "aannoy-o-tron": { animated: 1 },
  smorc: { srcset: 1 },
  tinyfin: { srcset: 1 },
  common: { srcset: 1 },
  rare: { srcset: 1 },
  epic: { srcset: 1 },
  legendary: { srcset: 1 },
  boombot: { srcset: 1 },
  horde: { srcset: 1 },
  alliance: { srcset: 1 },
  snake: { srcset: 1 },
  boomS: { srcset: 1 },
  chicken: { srcset: 1 },
  wow: { srcset: 1 },
  finleySmile: { srcset: 1 },
  reno: { srcset: 1 },
  illidon: { srcset: 1 },
  bob: { srcset: 1 },
  cat: { srcset: 1 },
  shrugillL: { srcset: 1 },
  shrugillR: { srcset: 1 },
  voidw: { srcset: 1 },
  blingtron: { srcset: 1 },
  dhguy: { srcset: 1 },
  rainbow: { srcset: 1 },
  faelin: { srcset: 1 },
  skelehead: { srcset: 1 },
  treeh: { srcset: 1 },
  ghoul: { srcset: 1 },
  pensive: { srcset: 1 },
  trumpet: { srcset: 1 },
  scared: { srcset: 1 },
  frogface: { srcset: 1 },
  thumbsup: { srcset: 1 },
  clowno: { srcset: 1 },
  fintective: { srcset: 1 },
  rafaamPog: { srcset: 1 },
  finking: { srcset: 1 },
  gulpfing: { srcset: 1 },
  gulpfingA: { srcset: 0, animated: 1 },
  shrimpossible: { srcset: 1 },
  finHUH: { srcset: 1 },
  kingPlush: { srcset: 1 },
  shocked: { srcset: 1 },
  waveflipper: { srcset: 1 },
  wavedummy: { srcset: 1 },
  happyButtons: { srcset: 1 },
  lynessa: { srcset: 1 },
  murlocMarine: { srcset: 1 },
  qnikkoHi: { srcset: 1 },
  aqnikkoHi: { srcset: 1, animated: 1 },
  treeLove: { srcset: 1 },
  qnikkoWhat: { srcset: 1 },
  qWOW: { srcset: 1 },
  aqWOW: { srcset: 1, animated: 1 },
  qLOVE: { srcset: 1 },
  qCRY: { srcset: 1 },
  qCozy: { srcset: 1 },
  qCool: { srcset: 1 },
  qTPie: { srcset: 1 },
  qSleep: { srcset: 1 },
};

const isYouTubeLink = (url) =>
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/.test(
    url,
  );

const isApprovedImage = (url) => /\.(jpeg|jpg|gif|png|webp)$/i.test(url);

const replaceEmotesAndLinks = (text) => {
  let result = text.replace(/\b(https?:\/\/\S+)\b/g, (match) => {
    if (isYouTubeLink(match)) {
      const videoId = match.match(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/,
      )[1];

      return `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen class="video-container"></iframe>`;
    }

    if (isApprovedImage(match)) {
      return `<p><a href="${match}" target="_blank" rel="noopener noreferrer"><img src="${match}" style="max-width: 100%; max-height: 350px; margin-top: 5px; border-radius: 5px;" /></a></p>`;
    }

    return `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });

  result = result.replace(
    /\[card id='(\d+)'](.*?)\[\/card]/g,
    (_, cardId, cardName) => {
      const localIcon =
        '<img src="/images/hearthcards-logo.webp" alt="local icon" class="local-icon" style="width: 20px; height: 20px; margin-right: 2px;">';
      return `<a href="/gallery/?selectedCard=${cardId}" rel="noopener noreferrer">${localIcon}${cardName}</a>`;
    },
  );

  result = result.replace(/\n/g, "<br>");

  return result.replace(/:([a-zA-Z0-9_-]+):/g, (match, emote) => {
    const emoteData = emoticonMap[emote];
    if (!emoteData) return match;

    const extension = emoteData.animated ? ".gif?v=1" : ".png";
    const src1x = `https://www.hearthcards.net/images/emotes/${emote}${extension}`;
    const src2x = `https://www.hearthcards.net/images/emotes/${emote}${
      emoteData.animated ? ".gif?v=1" : "2.png"
    }`;
    const src4x = `https://www.hearthcards.net/images/emotes/${emote}${
      emoteData.animated ? ".gif?v=1" : "3.png"
    }`;
    const srcset = emoteData.srcset
      ? `srcset="${src1x} 1x, ${src2x} 2x, ${src4x} 4x"`
      : "";

    return `<img src="${src1x}" ${srcset} alt="${emote}" class="emote-image" />`;
  });
};

const highlightTextInHtml = (htmlString, searchTerm) => {
  if (!searchTerm?.trim()) return htmlString;

  const container = document.createElement("div");
  container.innerHTML = htmlString;

  const regex = new RegExp(
    searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    "gi",
  );
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let currentNode;

  while ((currentNode = walker.nextNode())) {
    nodes.push(currentNode);
  }

  nodes.forEach((node) => {
    regex.lastIndex = 0;
    if (!regex.test(node.nodeValue)) return;

    regex.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    node.nodeValue.replace(regex, (match, offset) => {
      fragment.appendChild(
        document.createTextNode(node.nodeValue.substring(lastIndex, offset)),
      );

      const mark = document.createElement("mark");
      mark.className = "search-highlight";
      mark.textContent = match;
      fragment.appendChild(mark);

      lastIndex = offset + match.length;
      return match;
    });

    fragment.appendChild(
      document.createTextNode(node.nodeValue.substring(lastIndex)),
    );
    node.parentNode.replaceChild(fragment, node);
  });

  return container.innerHTML;
};

export const timeSince = (timeDifference) => {
  if (timeDifference === 0) return "just now";
  if (timeDifference < 60) {
    return `${timeDifference} second${timeDifference === 1 ? " ago" : "s ago"}`;
  }
  if (timeDifference < 3600) {
    const minutes = Math.floor(timeDifference / 60);
    return `${minutes} minute${minutes === 1 ? " ago" : "s ago"}`;
  }
  if (timeDifference < 86400) {
    const hours = Math.floor(timeDifference / 3600);
    return `${hours} hour${hours === 1 ? " ago" : "s ago"}`;
  }
  if (timeDifference < 2629746) {
    const days = Math.floor(timeDifference / 86400);
    return `${days} day${days === 1 ? " ago" : "s ago"}`;
  }
  if (timeDifference < 31536000) {
    const months = Math.floor(timeDifference / 2629746);
    return `${months} month${months === 1 ? " ago" : "s ago"}`;
  }

  const years = Math.floor(timeDifference / 31536000);
  return `${years} year${years === 1 ? " ago" : "s ago"}`;
};

export const processCommentText = (text, searchTerm) => {
  const safeInput = DOMPurify.sanitize(String(text ?? ""), {
    ALLOWED_TAGS: ["br", "a", "mark"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  });
  const enrichedHtml = replaceEmotesAndLinks(safeInput);
  const safeHtml = DOMPurify.sanitize(enrichedHtml, {
    ALLOWED_TAGS: ["br", "a", "mark", "img", "iframe", "p"],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "class",
      "src",
      "srcset",
      "alt",
      "style",
      "frameborder",
      "allowfullscreen",
    ],
  });

  return highlightTextInHtml(safeHtml, searchTerm);
};
